import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../services/api'
import { 
  Key, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  ExternalLink,
  Info
} from 'lucide-react'

interface PlatformConfig {
  platform: string
  isConfigured: boolean
  isConnected: boolean
  lastSync?: string
  credentials: {
    [key: string]: string
  }
}

const Settings = () => {
  const queryClient = useQueryClient()
  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({})
  const [editingPlatform, setEditingPlatform] = useState<string | null>(null)
  const [formData, setFormData] = useState<{[key: string]: any}>({})

  const { data: platformConfigs, error: configError } = useQuery('platform-configs', () =>
    api.get('/api/settings/platforms').then(res => res.data.data),
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const testConnectionMutation = useMutation(
    (platform: string) => api.post(`/api/settings/platforms/${platform}/test`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('platform-configs')
      }
    }
  )

  const saveCredentialsMutation = useMutation(
    ({ platform, credentials }: { platform: string; credentials: any }) =>
      api.post(`/api/settings/platforms/${platform}/credentials`, credentials),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('platform-configs')
        setEditingPlatform(null)
      }
    }
  )

  const togglePasswordVisibility = (platform: string, field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [`${platform}-${field}`]: !prev[`${platform}-${field}`]
    }))
  }

  const handleSaveCredentials = (platform: string) => {
    const credentials = formData[platform] || {}
    saveCredentialsMutation.mutate({ platform, credentials })
  }

  if (configError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>
        <div className="card">
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <div className="text-sm">Settings data temporarily unavailable</div>
              <div className="text-xs mt-1">Please try again later</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const platformInstructions = {
    etsy: {
      title: 'Etsy Integration',
      description: 'Connect your Etsy shop to sync orders and revenue data.',
      steps: [
        'Go to the Etsy Developer Portal',
        'Create a new app or use an existing one',
        'Get your API Key and API Secret',
        'Enter your Shop ID (found in your Etsy shop URL)',
        'Test the connection to verify credentials'
      ],
      fields: [
        { key: 'api_key', label: 'API Key', type: 'text', required: true },
        { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
        { key: 'shop_id', label: 'Shop ID', type: 'text', required: true }
      ],
      helpUrl: 'https://www.etsy.com/developers/',
      helpText: 'Find your Shop ID in your Etsy shop URL: etsy.com/shop/YOUR_SHOP_NAME'
    },
    amazon: {
      title: 'Amazon Seller Central',
      description: 'Connect your Amazon Seller Central account to sync orders and revenue.',
      steps: [
        'Go to Amazon Seller Central',
        'Navigate to Apps & Services → Develop Apps',
        'Create a new app or use existing one',
        'Get your Client ID and Client Secret',
        'Enter your Selling Partner ID',
        'Provide your Refresh Token (if you have one)'
      ],
      fields: [
        { key: 'client_id', label: 'Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        { key: 'selling_partner_id', label: 'Selling Partner ID', type: 'text', required: true },
        { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: false }
      ],
      helpUrl: 'https://sellercentral.amazon.com/',
      helpText: 'You may need to complete OAuth flow to get refresh token'
    },
    tpt: {
      title: 'Teachers Pay Teachers',
      description: 'Connect your TPT account to sync sales data using secure scraping.',
      steps: [
        'Enter your TPT username and password',
        'The system will securely scrape your sales data',
        'Data is encrypted and stored securely',
        'Sync runs automatically every hour'
      ],
      fields: [
        { key: 'username', label: 'TPT Username', type: 'text', required: true },
        { key: 'password', label: 'TPT Password', type: 'password', required: true }
      ],
      helpUrl: 'https://www.teacherspayteachers.com/',
      helpText: 'Your credentials are encrypted and never stored in plain text'
    },
    printify: {
      title: 'Printify Integration',
      description: 'Connect your Printify account to sync product costs and inventory.',
      steps: [
        'Go to your Printify Dashboard',
        'Navigate to Settings → API Keys',
        'Generate a new API key',
        'Enter your Shop ID',
        'Test the connection to verify access'
      ],
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true },
        { key: 'shop_id', label: 'Shop ID', type: 'text', required: true }
      ],
      helpUrl: 'https://printify.com/app/settings/api-keys',
      helpText: 'Find your Shop ID in your Printify dashboard URL'
    }
  }

  const renderPlatformCard = (platform: string) => {
    const config = platformConfigs?.find((p: PlatformConfig) => p.platform === platform)
    const instructions = platformInstructions[platform as keyof typeof platformInstructions]
    const isEditing = editingPlatform === platform
    const isLoading = testConnectionMutation.isLoading || saveCredentialsMutation.isLoading

    return (
      <div key={platform} className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{instructions.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{instructions.description}</p>
          </div>
          <div className="flex items-center space-x-2">
            {config?.isConnected ? (
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Connected</span>
              </div>
            ) : config?.isConfigured ? (
              <div className="flex items-center text-yellow-600">
                <AlertCircle className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Configured</span>
              </div>
            ) : (
              <div className="flex items-center text-gray-500">
                <AlertCircle className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Not Configured</span>
              </div>
            )}
          </div>
        </div>

        {/* Setup Instructions */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Setup Instructions:</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
            {instructions.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
          <div className="mt-3">
            <a
              href={instructions.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Get Help
            </a>
            <p className="text-xs text-gray-500 mt-1">{instructions.helpText}</p>
          </div>
        </div>

        {/* Credentials Form */}
        {isEditing ? (
          <div className="space-y-4">
            {instructions.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={field.type === 'password' && !showPasswords[`${platform}-${field.key}`] ? 'password' : 'text'}
                    value={formData[platform]?.[field.key] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      [platform]: { ...prev[platform], [field.key]: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter your ${field.label.toLowerCase()}`}
                  />
                  {field.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(platform, field.key)}
                      className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords[`${platform}-${field.key}`] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <div className="flex space-x-3 pt-2">
              <button
                onClick={() => handleSaveCredentials(platform)}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Credentials
              </button>
              <button
                onClick={() => setEditingPlatform(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex space-x-3">
            <button
              onClick={() => setEditingPlatform(platform)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Key className="w-4 h-4 mr-2" />
              {config?.isConfigured ? 'Update Credentials' : 'Configure Platform'}
            </button>
            {config?.isConfigured && (
              <button
                onClick={() => testConnectionMutation.mutate(platform)}
                disabled={testConnectionMutation.isLoading}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {testConnectionMutation.isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Info className="w-4 h-4 mr-2" />
                )}
                Test Connection
              </button>
            )}
          </div>
        )}

        {/* Last Sync Info */}
        {config?.lastSync && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Last sync: {new Date(config.lastSync).toLocaleString()}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Platform Settings</h1>
        <p className="text-gray-600">
          Configure your platform integrations to sync sales data automatically.
        </p>
      </div>

      <div className="grid gap-6">
        {Object.keys(platformInstructions).map(renderPlatformCard)}
      </div>

      {/* Security Notice */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Security & Privacy</h4>
            <p className="text-sm text-blue-800">
              All credentials are encrypted and stored securely. We never store your passwords in plain text. 
              Data is synced automatically and you can revoke access at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings 